import React, { useState, useEffect } from 'react'
import { apiClient, getLottoBallColorHex, getReadableTextColor } from '@workspace/public-utils'
import { Seo } from '../../components/Seo'

interface GeneratedNumber {
  id: string
  numbers: number[]
  generated_at: string
  subscription_tier: string
  target_round?: number
}

const MyNumbersPage: React.FC = () => {
  const [generatedNumbers, setGeneratedNumbers] = useState<GeneratedNumber[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(20)
  const [total, setTotal] = useState<number>(0)
  const seo = <Seo title="내 번호 관리" canonicalPath="/dashboard/numbers" noindex />

  useEffect(() => {
    fetchGeneratedNumbers(page, perPage)
  }, [page, perPage])

  const fetchGeneratedNumbers = async (p: number, size: number) => {
    try {
      const skip = (p - 1) * size
      const response = await apiClient.get('/api/v1/auth/me/generated-numbers', {
        params: { skip, limit: size }
      })
      const data = response.data
      // Normalize to existing shape
      setGeneratedNumbers(
        (data.generated_numbers || []).map((it: any) => ({
          id: it.id,
          numbers: it.numbers,
          generated_at: it.generated_at,
          subscription_tier: '-',
          target_round: it.round_target,
        }))
      )
      setTotal(Number(data.total || 0))
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        {seo}
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">번호를 불러오는 중...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        {seo}
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null)
              setLoading(true)
              fetchGeneratedNumbers(page, perPage)
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {seo}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">내 번호 관리</h1>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">생성된 번호 내역</h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {generatedNumbers.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                아직 생성된 번호가 없습니다.
              </div>
            ) : (
              generatedNumbers.map((item) => (
                <div key={item.id} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">{item.subscription_tier}</span>
                    <span className="text-sm text-gray-500">
                      {new Date(item.generated_at).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    {item.numbers.map((number, index) => {
                      const bg = getLottoBallColorHex(number)
                      const fg = getReadableTextColor(bg)
                      return (
                        <span
                          key={index}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium"
                          style={{ backgroundColor: bg, color: fg }}
                        >
                          {number}
                        </span>
                      )
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              총 {total.toLocaleString()}개 / 페이지 {page}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >이전</button>
              <select
                className="px-2 py-1 border rounded"
                value={perPage}
                onChange={(e) => { setPage(1); setPerPage(parseInt(e.target.value, 10) || 10) }}
              >
                {[10, 20, 50].map(sz => (
                  <option key={sz} value={sz}>{sz}개씩</option>
                ))}
              </select>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                onClick={() => {
                  const maxPage = Math.max(1, Math.ceil(total / perPage))
                  setPage((p) => Math.min(maxPage, p + 1))
                }}
                disabled={page >= Math.max(1, Math.ceil(total / perPage))}
              >다음</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MyNumbersPage